import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Instagram, Mail, MessageSquare, Copy, Check, ExternalLink, Users, Save, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const DEFAULT_INSTRUCTIONS_URL = "https://inparadisebeauty.com/instagram-instructions";
const SETTING_KEY = "instagram_instructions_url";

interface ClientOption {
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
}

const InstagramInstructionsCard = () => {
  const { toast } = useToast();
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState(false);

  const [instructionsUrl, setInstructionsUrl] = useState<string>(DEFAULT_INSTRUCTIONS_URL);
  const [urlInput, setUrlInput] = useState<string>(DEFAULT_INSTRUCTIONS_URL);
  const [urlLoading, setUrlLoading] = useState(true);
  const [savingUrl, setSavingUrl] = useState(false);

  useEffect(() => {
    supabase
      .from("profiles")
      .select("user_id, full_name, email, phone")
      .order("full_name", { ascending: true })
      .then(({ data }) => setClients((data as ClientOption[]) || []));

    supabase
      .from("site_settings")
      .select("value")
      .eq("key", SETTING_KEY)
      .maybeSingle()
      .then(({ data }) => {
        const v = (data as any)?.value?.trim();
        if (v) {
          setInstructionsUrl(v);
          setUrlInput(v);
        }
        setUrlLoading(false);
      });
  }, []);

  const saveUrl = async () => {
    const cleaned = urlInput.trim();
    if (!cleaned) {
      toast({ title: "URL required", description: "Enter a valid URL.", variant: "destructive" });
      return;
    }
    try {
      // basic URL validation
      // eslint-disable-next-line no-new
      new URL(cleaned);
    } catch {
      toast({ title: "Invalid URL", description: "Must start with https:// or http://", variant: "destructive" });
      return;
    }
    setSavingUrl(true);
    try {
      const { data: existing } = await supabase
        .from("site_settings")
        .select("id")
        .eq("key", SETTING_KEY)
        .maybeSingle();
      if (existing) {
        await supabase
          .from("site_settings")
          .update({ value: cleaned, updated_at: new Date().toISOString() })
          .eq("id", existing.id);
      } else {
        await supabase.from("site_settings").insert({ key: SETTING_KEY, value: cleaned });
      }
      setInstructionsUrl(cleaned);
      toast({ title: "Saved", description: "Instructions URL updated." });
    } catch (err: any) {
      toast({ title: "Save failed", description: err?.message || "Unable to save", variant: "destructive" });
    } finally {
      setSavingUrl(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        c.full_name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q),
    );
  }, [clients, search]);

  const selected = clients.find((c) => c.user_id === selectedId);

  const message = `Hi${selected?.full_name ? " " + selected.full_name.split(" ")[0] : ""}! Share your fresh look on Instagram and earn a $5 credit toward your next visit. Step-by-step instructions: ${instructionsUrl}`;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(instructionsUrl);
    setCopied(true);
    toast({ title: "Link copied", description: "Instructions URL copied to clipboard." });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendSms = () => {
    if (!selected?.phone) {
      toast({ title: "No phone on file", description: "Pick a client with a phone number.", variant: "destructive" });
      return;
    }
    const url = `sms:${selected.phone}?&body=${encodeURIComponent(message)}`;
    window.location.href = url;
  };

  const handleSendEmail = () => {
    if (!selected?.email) {
      toast({ title: "No email on file", description: "Pick a client with an email address.", variant: "destructive" });
      return;
    }
    const subject = encodeURIComponent("Earn a $5 credit by sharing on Instagram");
    const body = encodeURIComponent(message);
    window.location.href = `mailto:${selected.email}?subject=${subject}&body=${body}`;
  };

  return (
    <Card className="bg-card border-border/50 max-w-xl">
      <CardHeader>
        <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
          <Instagram size={16} />
          Instagram Posting Instructions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-muted-foreground text-sm font-light leading-relaxed">
          Send a client a link to step-by-step instructions on how to post on Instagram and earn a $5 credit. Or print the QR code so clients can scan it in the studio.
        </p>

        {/* Admin steps */}
        <div className="p-4 border border-border/40 bg-muted/20 space-y-2">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-medium">How to use</p>
          <ol className="text-xs text-muted-foreground font-light leading-relaxed list-decimal list-inside space-y-1">
            <li>Pick a client from the dropdown below.</li>
            <li>Tap <strong className="text-foreground">Text</strong> to open your messages app, or <strong className="text-foreground">Email</strong> to open your mail app — message is pre-filled.</li>
            <li>Or have the client scan the QR code below to open the instructions on their phone.</li>
            <li>Tap <strong className="text-foreground">Copy Link</strong> to paste it anywhere (DM, note, etc.).</li>
          </ol>
        </div>

        {/* Editable instructions URL */}
        <div className="space-y-2">
          <Label className="text-xs tracking-wide uppercase text-muted-foreground">Instructions URL</Label>
          <div className="flex gap-2">
            <Input
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder={DEFAULT_INSTRUCTIONS_URL}
              disabled={urlLoading}
              className="bg-background border-border/50 font-mono text-xs"
            />
            <Button
              onClick={saveUrl}
              disabled={savingUrl || urlLoading || urlInput.trim() === instructionsUrl}
              className="text-xs tracking-widest uppercase shrink-0"
            >
              {savingUrl ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Where the QR code, Text, Email, and Copy Link all point. Change the domain or route here without redeploying.
          </p>
        </div>

        {/* Client selector */}
        <div className="space-y-2">
          <label className="text-xs tracking-wide uppercase text-muted-foreground flex items-center gap-1.5">
            <Users size={12} />
            Select Client
          </label>
          <Input
            placeholder="Search by name, email or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-background border-border/50"
          />
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="bg-background border-border/50">
              <SelectValue placeholder={`Choose a client (${filtered.length} available)`} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-xs text-muted-foreground">No clients match.</div>
              ) : (
                filtered.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>
                    <span className="font-medium">{c.full_name || "Unnamed"}</span>
                    {c.phone && <span className="text-muted-foreground ml-2 text-xs">{c.phone}</span>}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleSendSms}
            disabled={!selected}
            variant="outline"
            className="text-xs tracking-widest uppercase"
          >
            <MessageSquare size={14} className="mr-2" />
            Text
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={!selected}
            variant="outline"
            className="text-xs tracking-widest uppercase"
          >
            <Mail size={14} className="mr-2" />
            Email
          </Button>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            className="text-xs tracking-widest uppercase"
          >
            {copied ? <Check size={14} className="mr-2" /> : <Copy size={14} className="mr-2" />}
            {copied ? "Copied" : "Copy Link"}
          </Button>
          <Button
            asChild
            variant="outline"
            className="text-xs tracking-widest uppercase"
          >
            <a href={instructionsUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} className="mr-2" />
              Preview
            </a>
          </Button>
        </div>

        {/* QR Code */}
        <div className="flex flex-col items-center gap-3 p-5 border border-border/40 bg-background">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Scan to view instructions</p>
          <div className="p-3 bg-white">
            <QRCodeSVG value={instructionsUrl} size={180} level="M" includeMargin={false} />
          </div>
          <code className="text-[10px] text-muted-foreground break-all text-center max-w-full">
            {instructionsUrl}
          </code>
        </div>
      </CardContent>
    </Card>
  );
};

export default InstagramInstructionsCard;
